import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { TaskProvider } from "./context/TaskContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Home from "./pages/Home";
import Login from "./pages/Login";
import ProfilePage from "./pages/ProfilePage";
import ArticlesPage from "./pages/ArticlesPage";
import StoriesPage from "./pages/StoriesPage";
import AdminPage from "./pages/AdminPage";
import SettingsPage from "./pages/SettingsPage";
import Layout from './components/Layout';
import logo from './assets/logo_scheduler.png';
import { getRouterBasename, getFullUrl } from './utils/urlUtils';

interface ProtectedRouteProps {
  children: React.ReactNode;
  role?: string;
}

const ProtectedRoute = ({ children, role }: ProtectedRouteProps) => {
  const { isAuthenticated, user } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (role && user?.role !== role) {
    const onLogout = () => {
      localStorage.removeItem('authToken');
      window.location.href = getFullUrl('login');
    };

    return (
      <Layout
        logoSrc={logo}
        appName="Scheduler App"
        onLogout={onLogout}
      >
        <div className="flex items-center justify-center h-screen">
          <div className="text-center p-8 bg-white rounded-lg shadow-md">
            <p className="text-gray-700">No tienes permisos para acceder a esta página</p>
          </div>
        </div>
      </Layout>
    );
  }

  return <>{children}</>;
};

function App() {
  return (
    <div className="min-h-screen p-4 bg-gray-100">
      <AuthProvider>
        <TaskProvider>
          <Router basename={getRouterBasename()}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                path="/home"
                element={
                  <ProtectedRoute>
                    <Home />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/articulos"
                element={
                  <ProtectedRoute>
                    <ArticlesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/historias"
                element={
                  <ProtectedRoute>
                    <StoriesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/configuracion"
                element={
                  <ProtectedRoute>
                    <SettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute role="SUPERADMIN">
                    <AdminPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/" element={<Navigate to="/home" replace />} />
              <Route path="*" element={<Navigate to="/home" replace />} />
            </Routes>
          </Router>
        </TaskProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
